<script setup lang="ts">
const { t } = useI18n({ useScope: 'local' })

const { bathrooms = 0, bedrooms = 0 } = defineProps<{
  bathrooms?: number
  bedrooms?: number
}>()

const emits = defineEmits<{
  'update-rooms': [{ bathrooms: number; bedrooms: number }]
}>()

const group = reactive<
  Record<'bathrooms' | 'bedrooms', { category: string; count: number }>
>({
  bedrooms: {
    category: t('bedrooms'),
    count: bedrooms,
  },
  bathrooms: {
    category: t('bathrooms'),
    count: bathrooms,
  },
})

const handleClear = () => {
  group.bathrooms.count = 0
  group.bedrooms.count = 0
}

const handleSubmit = () => {
  emits('update-rooms', {
    bathrooms: group.bathrooms.count,
    bedrooms: group.bedrooms.count,
  })
}

watch([() => bathrooms, () => bedrooms], ([newBathrooms, newBedrooms]) => {
  group.bathrooms.count = newBathrooms
  group.bedrooms.count = newBedrooms
})
</script>

<template>
  <div class="mt-4 flex flex-col gap-4 lg:mt-0">
    <div class="mb-4 font-semibold lg:mb-0 lg:text-md">{{ t('rooms') }}</div>
    <div class="flex flex-col gap-4">
      <template v-for="(type, key) in group" :key="key">
        <div
          :data-testid="`search-filter-${key}`"
          class="flex items-center justify-between"
        >
          <p class="mb-0 text-md">
            {{ type.category }}
          </p>
          <div class="flex items-center">
            <BaseFormComponentsBaseInputNumber
              v-model="type.count"
              :name="key"
              placeholder="0"
              wrapper-class="w-full override-style-input"
            />
          </div>
        </div>
        <div v-if="key === 'bedrooms'" class="border-t" />
      </template>
    </div>
    <div class="mt-4 flex justify-between lg:mt-2">
      <button
        class="text-md font-bold text-gray-700 lg:text-xs lg:text-gray-500"
        type="button"
        @click="handleClear"
      >
        {{ t('eraseAll') }}
      </button>
      <button
        class="rounded bg-secondary-400 px-4 py-3 font-bold text-white lg:bg-transparent lg:p-0 lg:text-xs lg:text-gray-700"
        type="button"
        @click="handleSubmit"
      >
        {{ t('validate') }}
      </button>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "bathrooms": "Bathrooms",
    "bedrooms": "Bedrooms",
    "eraseAll": "Erase all",
    "rooms": "Rooms",
    "validate": "Validate"
  },
  "fr": {
    "bathrooms": "Salles de bain",
    "bedrooms": "Chambres",
    "eraseAll": "Tout effacer",
    "rooms": "Pièces",
    "validate": "Valider"
  }
}
</i18n>
